import {
  BookOpenText,
  ClipboardCheck,
  FolderCog,
  GraduationCap,
  Layers,
  Library,
  Trophy,
  Users,
  UsersRound,
} from 'lucide-react'

import { defineArea, defineModule } from '@/lib/module-routes'
import { LEARNING_PERMISSIONS } from '@/lib/permissions'

import { learningIndexRoute, learningRoute } from './learning-route'

import {
  contentLibraryRoute,
  courseCertificateRoute,
  courseDetailRoute,
  coursesRoute,
  leaderboardRoute,
  learningCourseAdminDetailRoute,
  learningCourseAdminRoute,
  learningModulePreviewRoute,
  learningTeamDetailRoute,
  learningTeamsRoute,
  learningUsersRoute,
  moduleCertificateRoute,
  modulePlayerRoute,
  myLearningRoute,
  myRecordRoute,
  trainingModulesRoute,
} from './routes'

const learningArea = defineArea({
  id: 'learning',
  label: 'Learning sections',
  product: 'learning',
  route: learningRoute,
})

declare module '@/lib/module-routes' {
  interface Areas {
    learning: typeof learningArea
  }
}

export const learningModule = defineModule({
  routes: [
    learningRoute,
    learningIndexRoute,
    myLearningRoute,
    coursesRoute,
    courseDetailRoute,
    modulePlayerRoute,
    trainingModulesRoute,
    contentLibraryRoute,
    leaderboardRoute,
    myRecordRoute,
    courseCertificateRoute,
    moduleCertificateRoute,
    learningUsersRoute,
    learningTeamsRoute,
    learningCourseAdminRoute,
    learningCourseAdminDetailRoute,
    learningTeamDetailRoute,
    learningModulePreviewRoute,
  ],
  products: [
    {
      id: 'learning',
      label: 'Learning',
      description: 'Courses, training, and records',
      icon: GraduationCap,
      order: 65,
      routes: [learningRoute],
    },
  ],
  areas: [learningArea],
  sections: {
    learning: [
      { id: 'learning', label: 'Learning', order: 10 },
      { id: 'admin', label: 'Admin', order: 20 },
    ],
  },
  navigation: {
    learning: [
      {
        to: '/$entityId/learning/my-learning',
        label: 'My Learning',
        icon: GraduationCap,
        permission: LEARNING_PERMISSIONS.read,
        section: 'learning',
        order: 10,
      },
      {
        to: '/$entityId/learning/courses',
        label: 'Courses',
        icon: Layers,
        permission: LEARNING_PERMISSIONS.read,
        section: 'learning',
        order: 20,
        activeOn: [
          '/$entityId/learning/courses/$courseId',
          '/$entityId/learning/courses/$courseId/modules/$moduleId',
        ],
      },
      {
        to: '/$entityId/learning/training-modules',
        label: 'Training Modules',
        icon: BookOpenText,
        permission: LEARNING_PERMISSIONS.read,
        section: 'learning',
        order: 30,
      },
      {
        to: '/$entityId/learning/content-library',
        label: 'Content Library',
        icon: Library,
        permission: LEARNING_PERMISSIONS.read,
        section: 'learning',
        order: 40,
      },
      {
        to: '/$entityId/learning/leaderboard',
        label: 'Leaderboard',
        icon: Trophy,
        permission: LEARNING_PERMISSIONS.read,
        section: 'learning',
        order: 50,
      },
      {
        to: '/$entityId/learning/my-record',
        label: 'My Record',
        icon: ClipboardCheck,
        permission: LEARNING_PERMISSIONS.read,
        section: 'learning',
        order: 60,
        activeOn: [
          '/$entityId/learning/my-record/courses/$courseId/certificate',
          '/$entityId/learning/my-record/courses/$courseId/modules/$moduleId/certificate',
        ],
      },
      {
        to: '/$entityId/learning/users',
        label: 'Users',
        icon: Users,
        permission: LEARNING_PERMISSIONS.manage,
        section: 'admin',
        order: 10,
      },
      {
        to: '/$entityId/learning/teams',
        label: 'Teams',
        icon: UsersRound,
        permission: LEARNING_PERMISSIONS.manage,
        section: 'admin',
        order: 20,
        activeOn: ['/$entityId/learning/teams/$teamId'],
      },
      {
        to: '/$entityId/learning/admin/courses',
        label: 'Courses',
        icon: FolderCog,
        permission: LEARNING_PERMISSIONS.manage,
        section: 'admin',
        order: 30,
        activeOn: ['/$entityId/learning/admin/courses/$courseId'],
      },
    ],
  },
})
